import React, { FC, useMemo, useState, useEffect, ReactNode } from 'react';
import { makePermissionMap } from '../utils/KitCommon';

export interface SessionState {
  prefLangLocale?: string;
  isLoggedIn: boolean;
  dataServer?: string;
  accountsAPI?: string;
  l10nUnits?: { length: string; volume: string };
  currencyCode?: string;
  currencySymbol?: string;
  companyID?: number;
  permissions?: Map<string, boolean>;
  supportEmail?: string;
  customerSupportNumber?: string;
  portalTimezone?: string;
  subdomain?: string;
  brandedLogoURL?: string;
  userInfo?: UserInfo;
  nosletMapCoordinates?: { lat: number; lon: number };
  corporateUrl?: string;
  instanceName?: string;
  videoTranscodingService?: string;
}

interface UserInfo {
  userFirstName: string;
  userFullName: string;
  userName: string;
  userRole: string;
}

interface SessionCtxData {
  session: Partial<SessionState>;
  updateSession: () => void;
}

const SessionContext = React.createContext<SessionCtxData>({
  session: {},
} as SessionCtxData);

const SessionProvider: FC = ({ children }: { children?: ReactNode }) => {
  const [sessionState, setSessionState] = useState<SessionState>({ isLoggedIn: false });
  useEffect(() => {
    fetch('/index.php/nghelper/getSession')
      .then((res) => res.json())
      .then(
        (result) => {
          const state = {
            prefLangLocale: result.locale,
            isLoggedIn: result.userID ? true : false,
            dataServer: result.dataserver,
            accountsAPI: result.accountsAPI,
            l10nUnits: { length: result.length_unit, volume: result.volume_unit },
            currencyCode: result.currency_code,
            currencySymbol: result.currency_symbol,
            companyID: result.company_id ? result.company_id : null,
            permissions: makePermissionMap(result.taskPermissions),
            supportEmail: result.support_email,
            customerSupportNumber: result.customer_support_number,
            portalTimezone: result.portalTimezone,
            subdomain: result.subdomain,
            brandedLogoURL: result.brandedLogo ? result.brandedLogo : null,
            userInfo: {
              userFirstName: result.userFirstName,
              userFullName: result.userFullName,
              userName: result.userName,
              userRole: result.role,
            },
            nosletMapCoordinates: {
              lat: result.mapData.map_latitude,
              lon: result.mapData.map_longitude,
            },
            corporateUrl: result.corporate_url,
            instanceName: result.instance,
            videoTranscodingService: result.videoTranscodingService,
          };
          setSessionState(state);
        },
        (error) => {
          console.error(error);
        }
      );
  }, []);

  const providerValue = useMemo(
    () => ({
      session: sessionState,
      updateSession: setSessionState as () => void,
    }),
    [sessionState]
  );

  return <SessionContext.Provider value={providerValue}>{children}</SessionContext.Provider>;
};

export { SessionContext, SessionProvider };
